import React from "react"
import Layout from "../../../components/layout"
import { Link } from "gatsby"

export default props => (
  <Layout location={props.location}>
    <div className="row">
      <div className="col-md-12">
        <div className="page-header">
          <h1>
            POST /spaces/{"{"}spaceId{"}"}/tasks
          </h1>
        </div>
        <p>
          Create a new task in a specified space. The task can include uploaded
          file(s). To upload a file with a task:
        </p>
        <ol>
          <li>
            Get the upload URL from /api/files/getuploadurl and note the{" "}
            <em>fileKey</em> value from the response.
          </li>
          <li>
            Use the <em>fileKey</em> from step one as the <em>fileId</em> in the
            request body.
          </li>
        </ol>
        <p />
        <h2>URL Parameters</h2>
        <div className="table-responsive">
          <table className="table table-striped table-bordered table-condensed">
            <tbody valign="top">
              <tr>
                <th>Name</th>
                <th>Description</th>
                <th>Location</th>
                <th>Required</th>
                <th>Default Value</th>
              </tr>
              <tr>
                <td>spaceId</td>
                <td>Unique identifier of the space.</td>
                <td>path</td>
                <td>Yes</td>
                <td />
              </tr>
              <tr>
                <td>notify</td>
                <td>
                  Boolean value that determines whether assigned users are
                  notified.
                </td>
                <td>query</td>
                <td>No</td>
                <td>true</td>
              </tr>
            </tbody>
          </table>
        </div>
        <h2>Body Parameters</h2>
        <div className="table-responsive">
          <table className="table table-striped table-bordered table-condensed">
            <tbody valign="top">
              <tr>
                <th>Name</th>
                <th>Description</th>
                <th>JSON type</th>
                <th>Required</th>
                <th>Default Value</th>
              </tr>
              <tr>
                <td>content</td>
                <td>
                  Object with task properties.
                  <ul>
                    <li>
                      <em>bodyText</em>: (string) message text to send
                    </li>
                    <li>
                      <em>data</em>: (array) array of objects representing
                      attached files
                      <ul>
                        <li>
                          <em>fileId</em>: (string) fileKey from{" "}
                          <Link to="/api/files/files_getuploadurl">
                            /files/getuploadurl
                          </Link>
                        </li>
                        <li>
                          <em>fileSize</em>: (int) size of file in bytes
                        </li>
                        <li>
                          <em>fileType</em>: (string) 'image', 'video', 'audio',
                          or (default) 'document'
                        </li>
                        <li>
                          <em>name</em>: (string) file name
                        </li>
                        <li>
                          <em>provider</em>: (string) 'native'
                        </li>
                        <li>
                          <em>providerFileType</em>: (string) MIME type
                        </li>
                        <li>
                          <em>thumbnailFile</em>: (string) fileKey of thumbnail
                          from{" "}
                          <Link to="/api/files/files_getuploadurl">
                            /files/getuploadurl
                          </Link>
                        </li>
                      </ul>
                    </li>
                    <li>
                      <em>description</em>: (string) optional description.
                    </li>
                    <li>
                      <em>status</em>: status of the task: 'pending' or 'completed'..
                    </li>
                  </ul>
                </td>
                <td>object</td>
                <td>Yes</td>
                <td />
              </tr>
              <tr>
                <td>assignees</td>
                <td>UserIds of task members.</td>
                <td>array</td>
                <td>Yes</td>
                <td />
              </tr>
              <tr>
                <td>dueDate</td>
                <td>Date the task is to be completed.</td>
                <td>string</td>
                <td>Yes</td>
                <td />
              </tr>
              <tr>
                <td>status</td>
                <td>Status of the task: 0 or 1.</td>
                <td>integer</td>
                <td>Yes</td>
                <td />
              </tr>
            </tbody>
          </table>
        </div>
        <h2>Authentication</h2>
        <p>JwtAuthenticator, Oauth2Authenticator, AnonymousAuthenticator</p>
        <h2>Authorization</h2>
        <p>PERM_TOPIC_UPDATE</p>
        <h2>Example Request</h2>
        <h4>URL</h4>
        <pre className="nginx">
          POST https://spacesapis.avayacloud.com/api/spaces/{"{"}spaceId{"}"}/tasks
        </pre>
        <h4>Headers</h4>
        <pre className="javascript">
          Authorization: "jwt
          eyJhbGciOiJSUzI1NiIsIdfgdfgnR5cCI6IkpXVCJ9.eyJ1c2VyX2lkX3NpZyI6ImxPWHpxNzVzR3J3X0xwWEMyRGRMakhyYXJlWVhyTzFMdEZHZkhkOWNkTXciLCJwcm9kdWN0X3R5cGUiOiJhY2NvdW50cyIsImxhc3R1cGRhdGV0aW1lIjoiMjAxNy0wMS0zMFQxMzo1ODo0Ny4yMDYiLCJpc3MiOiJ6YW5nLmlvIiwicHVibGlja2V5aWQiOiJhZ3h6Zm05dVpYTnVZVEl3TVRSeUdnc1NEVWRLZDNSUWRXSnNhV05MWlhrdfgdWdJQ2dsUEtFakFvTSIsImV4cCI6MTQ5NTM5MTIwMywidXNlcl9pZCI6ImFneHpmbTl1WlhOdVlUSXdNVFJ5RVFzU0JGVnpaWElZZ0lEQXMtX25fUW9NIiwidmVyIjoiMi4wIn0.SqZpw1uPIebqW3qlDsm0-DWYFKEH0SgPwtD-tSESWOF-5hLkZMqJH28ooWux2S7s9riIew7pTVPnZpTgJa5zHcEAG0gXSEJVOqmpGp_4ufM8YtZoZMpBFjyAkwyAt-ILkGUYIo0GU5WyzMPLV8YLXfgdfgOgkE2MGGOajBkjPmC2Db3pA0D0M9k-21EL1gwWBC5YIq0PDAkgulegwl6khRmB8irJQ-dfg-d-KV9eYfFpsCdJKRGzSfqg"
        </pre>
        <p>Or</p>
        <pre className="javascript">
          Authorization: "bearer f746696f5de4528u128ae2f274eea253e8a7943a"
        </pre>
        <h4>Body</h4>
        <pre className="json">
{`{
    "status": 0,
    "content": {
        "bodyText": "task + file",
        "description": "this is a task , enclosed a file",
        "data": [
            {
                "fileId": "80b65d25-d960-4356-ab4d-8b09590efcd0",
                "fileSize": 3239,
                "fileType": "document",
                "icon": "",
                "name": "env2 (1)",
                "provider": "native",
                "providerFileType": ""
            }
        ],
        "assignees": [],
        "dueDate": "",
        "status": "pending"
    }
}`}
        </pre>
        <h2>Example Response</h2>
        <pre className="json">
{` {
  "data": [
      {
          "sender": {
              "_id": "5efc5417aa4a347af96de290",
              "type": "user",
              "username": "testavimas1899@gmail.com",
              "displayname": "test test",
              "picture_url": "https://storage.googleapis.com/onesna/pictures/pfpic_testavimas1899_29bef4a8-99b0-4901-86c7-567a64d9dcbb"
          },
          "content": {
              "endTime": null,
              "mediaMode": "audio",
              "virtualEndTime": null,
              "meetingStatus": "active",
              "bodyText": "task + file",
              "description": "this is a task , enclosed a file",
              "data": [
                  {
                      "metaData": {
                          "paging": 0,
                          "prvwProvd": "gcs",
                          "stgeProvd": "gcs"
                      },
                      "keywords": [],
                      "convertStatus": 0,
                      "convertStart": null,
                      "nextSchedule": null,
                      "pages": 0,
                      "recordingId": null,
                      "type": null,
                      "provider": "native",
                      "providerFileType": "",
                      "fileType": "document",
                      "name": "env2 (1)",
                      "fileId": "80b65d25-d960-4356-ab4d-8b09590efcd0",
                      "icon": "",
                      "thumbnail": "",
                      "description": "",
                      "previewFile": "",
                      "fileSize": 3239,
                      "thumbnailFile": "",
                      "path": "https://storage.googleapis.com/spaces2020/logan%2F80b65d25-d960-4356-ab4d-8b09590efcd0?GoogleAccessId=spaces2020@spaces-2018.iam.gserviceaccount.com&Expires=1596728522&Signature=eKSZ66%2BSCkXVCX8mUaIBx%2FsIKwWcCXuxECFpFmuw1X3P8WuLzfJLi4G%2F0U4RFDvrtu3NCeBz5YVJKagb%2FgeJGCwb7Y63SmbypcKi5sd2NPfJVvHJmw0QLWjtBUi43D%2B1IZkP0n3dkpxl9QjiXd0ntsLQVF8Nm2wdyfUnpFPsjCnHhKF30Q1J62GETwEDb7Gv6cN8ZkOJpW2MEawCkeqcY2Mdz4%2Bji5IdWN4Cc1ShvuxscWgyTxZVsjzAcq4Tis0Sv3DOeWOdneLg7O4MQG%2FumTLIw5DsYitk4SAoidYrU6gyAqp3m7oGzXgoMEnR17VlbM0wEbLYloXv3vEbsCUgnw%3D%3D&response-content-disposition=attachment%3B%20filename%3D%22env2%20(1)%22"
                  }
              ],
              "assignees": [],
              "dueDate": "2020-08-06T14:11:19.979Z",
              "status": "pending",
              "startTime": "2020-08-06T14:11:19.992Z",
              "recordings": []
          },
          "chatCount": 0,
          "likeCount": 0,
          "status": 0,
          "_id": "5f2c0f8778479f00da0765c7",
          "category": "task",
          "topicId": "5f2276a8fe5c16664f01c5be",
          "created": "2020-08-06T14:11:19.974Z",
          "modified": "2020-08-06T14:11:19.974Z"
      }
  ]
}`}
        </pre>
      </div>
    </div>
  </Layout>
)
